import { Body, Vector } from 'matter-js';

/**
 * Clase base para entidades físicas en el BubbleField.
 * Cada entidad tiene un cuerpo físico (Matter.js) y un elemento DOM asociado.
 * La sincronización entre ambos se realiza en cada frame de animación.
 */
export abstract class AbstractEntity<DatumType = any> {
    public readonly _id: string;
    /** Mutar solo a través de {@link _SetDatum} para mantener trazabilidad de los cambios */
    public _datum: DatumType;
    public _body: Body;
    public _element: HTMLElement;

    /** CORRECTOR DE POSICIÓN */
    private destinationPositionRelativeToCenter: Vector | null = null; // Destino de corrección desde el centro del cuerpo físico (DOM)
    private centerOffsetPosition: Vector | null = null; // Posición actual desde el centro del cuerpo físico (DOM)
    private speedCorrectionFactor: number = 0.15; // Velocidad de aproximación a la posición de corrección

    // SISTEMA DE ESCALADO
    protected currentScale: number = 1;   // Escala visual actual
    protected targetScale: number = 1;    // Escala a la que queremos llegar
    protected scaleSpeed: number = 0.15;  // Velocidad de interpolación (0.1 = lento, 0.5 = rápido)

    // SISTEMA DE LANZAMIENTO GUIADO
    protected launchDestination: Vector | null = null;  // Destino activo
    protected launchSpeed: number = 0.15;               // Velocidad de aproximación al destino
    private launchStartTime: number = 0;                // Timestamp de inicio del lanzamiento
    private launchMaxDuration: number = 2000;           // Duración (ms) máxima del lanzamiento para evitar viajes infinitos

    // BACKUP DE ESTADO: Guarda las propiedades antes de modificar la burbuja
    protected preActivationState: Partial<Record<typeof SAVE_PROPS[number], any>> | null = null;

    constructor(element: HTMLElement, body: Body, datum: DatumType) {
        this._element = element;
        this._body = body;
        this._id = String(body.id);
        this._datum = datum;

        // Vinculación bidireccional: Permite acceder a la entidad desde el cuerpo físico
        // para detectar colisiones y saber qué elemento DOM tocar
        this._body._entity = this;
        // Vinculación inversa: Permite acceder a la entidad desde el elemento DOM
        // (útil para manejo de teclado/accesibilidad vía delegación de eventos)
        this._element._entity = this;
    }

    // *************************************************************************
    //#region API

    /** Actualiza el datum asociado a la entidad (ej: al refrescar sus datos desde afuera) */
    public _SetDatum(datum: DatumType): void {
        this._datum = datum;
    }

    /**
     * Define la nueva escala objetivo.
     * Es interrumpible si se invoca de nuevo antes de llegar a la escala anterior.
     */
    public _SetTargetScale(newScale: number, speed: number = 0.15) {
        this.targetScale = newScale;
        this.scaleSpeed = speed;
        // Nota: el loop '_Sync' se encargará de acercar currentScale a targetScale en cada frame
    }

    /** Establece la nueva posición del cuerpo físico */
    public _SetPosition(position: Vector) {
        this._CancelLaunch(); // Si se está a medio lanzamiento guiado, cancelar para respetar esta nueva posición
        Body.setPosition(this._body, position);
    }

    /** Inicia una corrección de posición del DOM hacia una coordenada relativa al centro del cuerpo físico */
    public _LaunchCorrectionDOMTo(x: number, y: number, speed: number = 0.15) {
        this.destinationPositionRelativeToCenter = { x, y };
        this.speedCorrectionFactor = speed;
    }

    /**
     * Inicia un viaje guiado hacia una coordenada.
     * La burbuja ajustará su velocidad en cada frame para asegurar la llegada.
     */
    public _LaunchTo(x: number, y: number, speed: number = 0.15, collisionsEnabled: boolean = true) {
        this.launchDestination = { x, y };
        this.launchSpeed = speed;

        // Guardar estado si NO estamos ya en medio de un viaje
        this.savePreActivationState();

        // Registrar el tiempo de inicio del lanzamiento
        this.launchStartTime = Date.now();

        // validar que el cuerpo no esté estático para poder moverlo
        if (this._body.isStatic) {
            Body.setStatic(this._body, false);
        }
        this._body.frictionAir = 0; // Quitar fricción para que el cálculo sea exacto
        if (!collisionsEnabled) {
            // sin colisiones durante el lanzamiento
            this._body.collisionFilter.mask = 0; // No colisiona con nada
        }
    }

    /**
     * Interrumpe el viaje actual (ej: si el usuario agarra la burbuja a medio camino)
     */
    public _CancelLaunch() {
        if (this.launchDestination) {
            this.launchDestination = null;
            this.restorePreLaunchState();
        }
    }

    /** Establece si la entidad es cinemática (no afectada por fuerzas externas) */
    public _SetKinematic(isKinematic: boolean) {
        if (isKinematic) {
            Body.setStatic(this._body, true);
            this._SetCollisionsEnabled(false);
        } else {
            Body.setStatic(this._body, false);
            // Restaurar máscara de colisión según el tipo de entidad (esto es un ejemplo, ajusta según tus categorías)
            this._SetCollisionsEnabled(true);
        }
    }

    public _SetCollisionsEnabled(enabled: boolean) {
        if (enabled) {
            this._body.collisionFilter.mask = (this as any).__maskBeforeCollisionToggle || 0xFFFFFFFF; // Restauramos la máscara guardada o la máscara por defecto
        } else {
            (this as any).__maskBeforeCollisionToggle = this._body.collisionFilter.mask; // Guardamos la máscara actual para restaurarla después
            this._body.collisionFilter.mask = 0; // No colisiona con nada
        }
    }

    /**
     * Sincroniza la posición física con el DOM.
     * Se debe llamar en cada frame de animación (60fps).
     */
    public _Sync(): void {
        const { x, y } = this._body.position;

        // LÓGICA DE LANZAMIENTO GUIADO
        if (this.launchDestination) {
            const diffX = this.launchDestination.x - x;
            const diffY = this.launchDestination.y - y;
            const distance = Math.sqrt(diffX * diffX + diffY * diffY);

            // tolerancia de llegada (2px es imperceptible visualmente)
            if (distance < 2.0 || (Date.now() - this.launchStartTime) > this.launchMaxDuration) {
                // Llegada: Lo clavamos en el pixel exacto y frenamos en seco
                Body.setPosition(this._body, this.launchDestination);
                Body.setVelocity(this._body, { x: 0, y: 0 });
                this.launchDestination = null; // Viaje terminado
                this.restorePreLaunchState(); // Devolver al estado original
            } else {
                // Acercamiento dinámico. Diferencia * velocidad: la burbuja frena suavemente al acercarse.
                Body.setVelocity(this._body, {
                    x: diffX * this.launchSpeed,
                    y: diffY * this.launchSpeed
                });
            }
        }

        // 2. LÓGICA LERP (Suavizado matemático)
        // Si no está en la escala objetivo, se acerca un porcentaje
        if (Math.abs(this.targetScale - this.currentScale) > 0.001) {
            // Fórmula: actual + (destino - actual) * velocidad
            this.currentScale += (this.targetScale - this.currentScale) * this.scaleSpeed;
        } else {
            // Si está cerca, forzar el valor final para dejar de calcular
            this.currentScale = this.targetScale;
        }

        // 3. LÓGICA DE CORRECCIÓN DE POSICIÓN DEL DOM
        let relatPosX = this.centerOffsetPosition?.x || 0;
        let relatPosY = this.centerOffsetPosition?.y || 0;
        if (this.centerOffsetPosition?.x === this.destinationPositionRelativeToCenter?.x &&
            this.centerOffsetPosition?.y === this.destinationPositionRelativeToCenter?.y) {
            this.destinationPositionRelativeToCenter = null; // Ya está en el destino
        }
        if (this.destinationPositionRelativeToCenter) {
            if (!this.centerOffsetPosition) {
                this.centerOffsetPosition = { x: 0, y: 0 }; // Inicializamos la posición actual si no existe
            }
            const diffX = this.destinationPositionRelativeToCenter.x - this.centerOffsetPosition.x;
            const diffY = this.destinationPositionRelativeToCenter.y - this.centerOffsetPosition.y;

            // Acercamiento dinámico
            this.centerOffsetPosition.x += diffX * this.speedCorrectionFactor;
            this.centerOffsetPosition.y += diffY * this.speedCorrectionFactor;

            // Si está cerca del destino, forzamos el valor final y detenemos la corrección
            if (Math.sqrt(diffX * diffX + diffY * diffY) < 0.5) {
                this.centerOffsetPosition = { ...this.destinationPositionRelativeToCenter };
                this.destinationPositionRelativeToCenter = null; // Corrección terminada
            }

            relatPosX = this.centerOffsetPosition.x;
            relatPosY = this.centerOffsetPosition.y;
        }

        // RENDERIZADO COMPUESTO
        // Usa translate3d para forzar la aceleración por GPU
        // El translate(-50%, -50%) centra el elemento en la posición del cuerpo físico
        this._element.style.transform =
            `translate3d(${x}px, ${y}px, 0) translate(calc(-50% + ${relatPosX}px), calc(-50% + ${relatPosY}px)) scale(${this.currentScale})`;
    }

    /**
     * Método para limpiar referencias al destruir la entidad
     */
    public _Destroy(): void {
        this._element.remove();
    }

    //#endregion
    // *************************************************************************

    // *************************************************************************
    //#region MÉTODOS PRIVADOS

    private savePreActivationState(force: boolean = false) {
        if (this.preActivationState && !force) return;
        this.preActivationState = {};
        for (const saveProp of SAVE_PROPS) {
            const saveValue = this._body[saveProp];
            if (typeof saveValue !== 'object') { // solo propiedades primitivas
                this.preActivationState[saveProp] = saveValue;
            } else if (saveProp === 'collisionFilter') { // caso especial
                this.preActivationState.collisionFilter = { ...saveValue };
            }
        }
    }

    /**
     * Helper para devolver el cuerpo a su estado original
     */
    private restorePreLaunchState() {
        if (!this.preActivationState) return;
        for (const prop in this.preActivationState) {
            const savedValue = this.preActivationState[prop];

            // primitivo: se pueden pasar directamente al body
            const directProps: SaveProps[] = ['frictionAir', 'friction', 'velocity', 'density', 'restitution', 'mass'];
            if (directProps.includes(prop as SaveProps)) {
                this._body[prop] = savedValue;
            }
            // primitivo: necesitan una función específica para ser restaurados
            const specialProps: SaveProps[] = ['isStatic'];
            if (specialProps.includes(prop as SaveProps)) {
                if (prop === 'isStatic') {
                    Body.setStatic(this._body, savedValue);
                }
            }
            // objetos
            if (prop === 'collisionFilter') {
                if (savedValue) {
                    for (const key in savedValue) {
                        this._body.collisionFilter[key] = savedValue[key];
                    }
                } else {
                    this._body.collisionFilter = savedValue; // null | undefined
                }
            }
        }
        this.preActivationState = null;
    }

    //#endregion
    // *************************************************************************
}


const SAVE_PROPS = [
    // primitivo: se pueden pasar directamente al body
    "frictionAir",
    "friction",
    "velocity",
    "density",
    "restitution",
    "mass",
    // primitivo: necesitan una función específica para ser restaurados
    "isStatic",
    // objetos
    "collisionFilter",
] as const;

type SaveProps = typeof SAVE_PROPS[number];
type IBodyDefinitionKeys = keyof Matter.IBodyDefinition;
type ValidateSaveProps = typeof SAVE_PROPS[number] extends IBodyDefinitionKeys ? true : never;
const VERIFICA_LA_CONFIG_DE_SAVED_PROPS: ValidateSaveProps = true; // Si esto da error, revisa que las claves de SAVE_PROPS sean válidas en Matter.IBodyDefinition
(VERIFICA_LA_CONFIG_DE_SAVED_PROPS);
